import {
  type EventStandardDefs,
  type FeatureStandardDefs,
  type UserFeatureStandardDefs,
  version as bundledVersion,
} from '@solinkcorp/event-standard';
import { type FeatureTagEntry } from '@solinkcorp/event-standard/lib/src/types';
import { toastUnexpectedError, useInterval } from '@solinkcorp/solink-components';
import { CallhomeApi, StorageAPI } from 'clients';

import React, { useContext, useEffect, useState } from 'react';

// If true, force the event standard to be loaded from the local package
// instead of from call-home-server.  This is useful for testing new
// event standards.
const FORCE_EVENT_STANDARD_FROM_LOCAL_PACKAGE = false;

/**
 *  The key to use to cache event standard (and friends) in local storage.
 *
 * DO NOT EXPORT THIS KEY!  If you need access to EventStandard, please use the
 * eventStandardCache API instead of trying to read EventStandard from localStorage
 * directly.  Reading it directly creates a hidden dependency and a tight
 * coupling between your module and this one.
 *
 * It will also cause us to have multiple copies of EventStandard in memory.
 * EventStandard gets updated from time to time, and if you have your own private
 * cached copy you pulled from local storage, your copy won't get updated.
 *
 * we also persist event standard if the new impersonated account is under the same customer
 * the customerId already in local storage
 */
const PRIVATE_DO_NOT_EXPORT__STANDARDS_KEY = 'PERSISTENT_EVENT_STANDARDS';

// How often to fetch event standard.
const FETCH_INTERVAL_MS = 15 * 60 * 1000;
// How often to retry fetching event standard if the last attempt errored out.
const FETCH_INTERVAL_ERROR_MS = 60 * 1000;

// These are old keys that were used by previous versions of video-discovery.
const EVENT_STANDARD = 'EVENT_STANDARD';
const FEATURE_STANDARD = 'FEATURE_STANDARD';
const EVENT_STANDARD_VERSION = 'EVENT_STANDARD_VERSION';

interface Standards extends StandardsDefs {
  customerId: string;
}

export interface StandardsDefs {
  eventStandard: EventStandardDefs;
  featureStandard: FeatureStandardDefs;
  userFeatureStandard: UserFeatureStandardDefs;
  version: string;
}

const StandardsContext = React.createContext<{ standards: Standards | undefined } | undefined>(
  undefined,
);

interface EventStandardProviderProps {
  customerId?: string;
  children: React.ReactNode;
}

/**
 * Provides the event standard and feature standard to all descendants.
 *
 * The event standard and feature standard will be updated automatically if they
 * change.  We check for a new version approximately every 15 minutes.
 */
export const EventStandardProvider: React.FC<EventStandardProviderProps> = ({
  customerId,
  children,
}) => {
  const [state, setState] = useState<{ standards: Standards | undefined }>({
    standards: undefined,
  });

  const handleNewStandards = (standards: Standards | undefined) => {
    if (standards) {
      setState({ standards });
      StorageAPI.storeLocally(PRIVATE_DO_NOT_EXPORT__STANDARDS_KEY, standards);
    }
  };

  // If the customerId changes, immediately fetch the event standard.
  useEffect(() => {
    // Try to fetch from the cache.
    const cachedStandards =
      customerId && !FORCE_EVENT_STANDARD_FROM_LOCAL_PACKAGE
        ? fetchCachedEventStandard(customerId)
        : undefined;
    setState({ standards: cachedStandards });

    if (customerId) {
      if (FORCE_EVENT_STANDARD_FROM_LOCAL_PACKAGE) {
        loadEventStandardFromVideoDiscovery(customerId)
          .then(handleNewStandards)
          .catch(toastUnexpectedError);
      } else {
        // Fetch the up-to-date version from call-home.
        loadEventStandardFromCallHome(customerId, cachedStandards)
          .catch((err) => {
            console.error(`Error loading eventStandard from call-home`, err);
            // If the initial fetch from call-home fails, fall back to the version bundled with video-discovery-web.
            return loadEventStandardFromVideoDiscovery(customerId);
          })
          .then(handleNewStandards)
          .catch(toastUnexpectedError);
      }
    }
  }, [customerId]);

  // Periodically fetch the event standard from call-home-server to see if it has changed.
  let retryTime: number | undefined;
  if (!customerId) {
    // No customerId, can't fetch event standard yet.
  } else if (!state.standards) {
    // No cached standards loaded - retry once a minute.
    retryTime = FETCH_INTERVAL_ERROR_MS;
  } else {
    // Re-fetch the event cache once every 15 minutes.
    retryTime = FETCH_INTERVAL_MS;
  }

  useInterval(() => {
    if (customerId && !FORCE_EVENT_STANDARD_FROM_LOCAL_PACKAGE) {
      loadEventStandardFromCallHome(customerId, state.standards)
        .then(handleNewStandards)
        .catch((err) => console.warn(`Failed to update event standard`, err));
    }
  }, retryTime);

  return <StandardsContext.Provider value={state}>{children}</StandardsContext.Provider>;
};

/**
 * Returns the event standard and feature standard.
 */
export function useEventStandards(): StandardsDefs | undefined {
  const standardsWrapper = useContext(StandardsContext);
  if (!standardsWrapper) {
    throw new Error(`Trying to use event standard without a <EventStandardProvider/>!`);
  }

  const { standards } = standardsWrapper;
  return standards;
}

export function useEventStandard(): EventStandardDefs | undefined {
  return useEventStandards()?.eventStandard;
}

export function useFeatureStandard(): FeatureStandardDefs | undefined {
  return useEventStandards()?.featureStandard;
}

export function useUserFeatureStandard(): UserFeatureStandardDefs | undefined {
  return useEventStandards()?.userFeatureStandard;
}

export function useDeviceTagsStandard(): Record<string, FeatureTagEntry> | undefined {
  const featureStandard = useFeatureStandard();
  if (!featureStandard) {
    return undefined;
  }

  const deviceTagsStandard: Record<string, FeatureTagEntry> = {};
  for (const [tag, tagDef] of Object.entries(featureStandard.tags)) {
    if (tagDef.applicableTo === 'devices') {
      deviceTagsStandard[tag] = tagDef;
    }
  }
  return deviceTagsStandard;
}

/** Fetch the latest event standard from call-home-server. */
async function loadEventStandardFromCallHome(
  customerId: string,
  existing?: Standards,
): Promise<Standards | undefined> {
  if (!customerId) {
    throw new Error(`Can't load event standard without a customerId`);
  }

  // Fetch the latest version from call-home
  const version = await CallhomeApi().getEventStandardVersion(customerId);
  if (existing?.version === version.version) {
    // We already have the latest version.  Don't bother re-fetching.
    return undefined;
  }

  const [eventStandard, featureStandard, userFeatureStandard] = await Promise.all([
    CallhomeApi().getEventStandard(customerId),
    CallhomeApi().getFeatureStandard(customerId),
    CallhomeApi().getUserFeatureStandard(customerId),
  ]);

  return {
    customerId,
    eventStandard,
    featureStandard,
    userFeatureStandard,
    version: version.version,
  };
}

/**
 * Fetch the event-standard that was bundled with video-discovery-web.  This is
 * maybe going to be an old version, but it's better than nothing.
 */
async function loadEventStandardFromVideoDiscovery(customerId: string): Promise<Standards> {
  // Note that we CANNOT do:
  //
  //     const { eventStandard, featureStandard } = await import('@solinkcorp/event-standard');
  //
  // here, because this will cause Rollup to include all of event-standard in the main index.js file.

  const eventStandard: EventStandardDefs = (
    await import('@solinkcorp/event-standard/lib/build/eventStandard.json.js')
  ).default as EventStandardDefs;

  const featureStandard: FeatureStandardDefs = (
    await import('@solinkcorp/event-standard/lib/build/featureStandard.json.js')
  ).default as FeatureStandardDefs;

  const userFeatureStandard: UserFeatureStandardDefs = (
    await import('@solinkcorp/event-standard/lib/build/userFeatureStandard.json.js')
  ).default as UserFeatureStandardDefs;

  return {
    customerId,
    eventStandard,
    featureStandard,
    userFeatureStandard,
    version: bundledVersion,
  };
}

/** Fetch event standard from the local cache. */
function fetchCachedEventStandard(customerId: string): Standards | undefined {
  // If old versions are stored, remove them.
  StorageAPI.removeLocally(EVENT_STANDARD);
  StorageAPI.removeLocally(FEATURE_STANDARD);
  StorageAPI.removeLocally(EVENT_STANDARD_VERSION);

  const result = StorageAPI.getLocally<Standards>(PRIVATE_DO_NOT_EXPORT__STANDARDS_KEY);

  if (!result) {
    return undefined;
  }

  if (!result.customerId || !result.eventStandard || !result.featureStandard || !result.version) {
    console.warn(`Corrupted event standard in local storage`);
    return undefined;
  }

  // We pass the customerId up to call-home-server when we fetch event standard. Note that
  // call-home-server doesn't actually return different event standards for different
  // customers...  But it COULD, so we store the customerId in the cache,
  // and throw it away the cache if it was loaded for some other customer.
  if (result.customerId !== customerId) {
    // Event standard was for some other customer...
    StorageAPI.removeLocally(PRIVATE_DO_NOT_EXPORT__STANDARDS_KEY);
    return undefined;
  }

  return result;
}
